/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import { styled } from '@compiled/react';
import { layers } from '@jira/platform__ui-layers/src/layers.tsx';
import ErrorBoundary from '@jira/platform__error-boundary/src/ErrorBoundary.tsx';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api/use-current-preview-panel';
import { fg } from '@atlassian/jira-feature-gating';
import { usePanelManager } from '@atlassian/panel-system';
import {
	useBottomRightCornerActions,
	useBottomRightCornerContainer,
} from '../../../controllers/layout-controller/consumers/bottom-right-corner-controller/index.tsx';
import { useActiveRightSidebarState } from '../../../controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useDetailPanelWidth } from '../../../controllers/layout-controller/store/index.tsx';
import type { ProviderProps, GlobalBottomRightCornerContainerProps } from './types.tsx';

/**
 * Adjusts a container's position dynamically in relation to the state of a right sidebar and given offset parameters. This ensures the container is always correctly positioned, taking into account the sidebar's visibility and size.
 */
export function ContainerWithRef({
	bottomOffset,
	rightOffset,
	children,
}: Required<GlobalBottomRightCornerContainerProps> & {
	children?: React.ReactNode;
}): React.JSX.Element {
	const [{ width }] = useDetailPanelWidth();
	const rightSidebar = useActiveRightSidebarState();
	const { registerBottomRightCornerContainer } = useBottomRightCornerActions();
	const { state: panelState } = usePanelManager();

	// Currently the ADS panel has a fixed width, but this will be updated to be dynamic
	const adsPanelWidth = 364;

	const isSidebarOpen = rightSidebar && !rightSidebar.isCollapsed && !rightSidebar.isMinimised;
	const rightSidebarWidth = isSidebarOpen ? rightSidebar.width : 0;

	const rightSidebarOffset = isSidebarOpen ? rightSidebarWidth : 0;
	let distanceFromRightSidebar = width + rightOffset + rightSidebarOffset;
	/**
	 * Preview panel also opens inside RightPanel and is resizable. And it has default width of 400px,
	 * which is more than width of RightSidebar (386px)
	 */
	// Preview panel takes precedence over sidebar when open
	if (fg('jira-glance-panel')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const panel = useCurrentPreviewPanel();
		if (panel) {
			distanceFromRightSidebar = panel.width + rightOffset;
		}
	}

	if (fg('ads-panel-system-jira-admin')) {
		if (panelState.activePanels.length) {
			distanceFromRightSidebar = adsPanelWidth + rightOffset;
		}
	}

	return children ? (
		<StyledContainer
			ref={registerBottomRightCornerContainer}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="layout-controller.ui.bottom-right-corner.container.styled-container"
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				right: `calc(${distanceFromRightSidebar}px + ${token('space.300')})`,
				bottom: `calc(${token('space.300')} + ${bottomOffset}px)`,
			}}
		>
			{children}
		</StyledContainer>
	) : (
		<StyledContainer
			ref={registerBottomRightCornerContainer}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="layout-controller.ui.bottom-right-corner.container.styled-container"
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				right: `calc(${distanceFromRightSidebar}px + ${token('space.300')})`,
				bottom: `calc(${token('space.300')} + ${bottomOffset}px)`,
			}}
		/>
	);
}
export const GlobalBottomRightCornerContainer = ({
	bottomOffset = 0,
	rightOffset = 0,
	children,
}: GlobalBottomRightCornerContainerProps & { children?: React.ReactNode }): React.JSX.Element => (
	<ErrorBoundary
		id="bottom-right-corner-container"
		packageName="jira-bottom-right-corner-controller"
	>
		{children ? (
			<ContainerWithRef bottomOffset={bottomOffset} rightOffset={rightOffset}>
				{children}
			</ContainerWithRef>
		) : (
			<ContainerWithRef bottomOffset={bottomOffset} rightOffset={rightOffset} />
		)}
	</ErrorBoundary>
);
export const GlobalBottomRightCornerContainerProvider = ({
	children,
}: ProviderProps): React.JSX.Element => {
	const container = useBottomRightCornerContainer();
	return (
		<UFOSegment name="global-bottom-right-corner">
			{container && children}
			<GlobalBottomRightCornerContainer />
		</UFOSegment>
	);
};

export const BottomRightInlineItem = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	return <Box xcss={styles.inlineInteractiveChild}>{children}</Box>;
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div({
	position: 'fixed',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.navigation - 1,
	overflow: 'visible',
	alignItems: 'flex-end',
	pointerEvents: 'none',
});
StyledContainer.displayName = 'StyledContainer';

const styles = cssMap({
	inlineInteractiveChild: {
		pointerEvents: 'auto',
		// matches outlet spacing
		marginLeft: token('space.200'),
	},
});
